<template>
  <div class="cartItem" :class="{firstItem: order.firstItem}">
    <div class="requestAt mb5 notranslate">
      <i class="orderId">{{locale.reservation_order_id}} {{order.order_id}}</i>
      <p class="paymentStatus">
        {{status(order.order_status)}}: {{setDateTimeFormat(order.createat)}}
      </p>
    </div>
    <div class="inner">
      <div class="row">
        <span class="label toCapitalize notranslate">{{locale.reservation_use_space}}</span>
        <div class="value">{{summary.space.name[lang]}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_use_date}}</span>
        <div class="value">{{summary.date}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_use_time}}</span>
        <div class="value">{{summary.time.name}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_use_person}}</span>
        <div class="value">{{summary.person}}</div>
      </div>
      <div class="row">
        <span class="label toCapitalize notranslate">{{locale.reservation_use_section}}/{{locale.reservation_use_place}}</span>
        <div v-if="!autoAssign" class="value">
          <div v-for="(info, index) in seats"
            :key="index">
            {{info.section.name[lang]}}
            <span v-if="showPlaceName || showSeatName">
              -
              <span v-if="showPlaceName">{{info.place.name[lang]}}</span>
              <span v-if="showPlaceName && showSeatName"> / </span>
              <span v-if="showSeatName">{{info.seat.name[lang]}}</span>
            </span>
          </div>
        </div>
        <div v-else-if="seats && seats.section" class="value">
          {{ seats.section.name[lang] }} / {{ seats.place.name[lang] }}
        </div>
        <div v-else class="value">-</div>
      </div>
      <div v-if="summary.firstName" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_first_name}}</span>
        <div class="value">{{summary.firstName}}</div>
      </div>
      <div v-if="summary.lastName" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_last_name}}</span>
        <div class="value">{{summary.lastName}}</div>
      </div>
      <div v-if="summary.phone" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_phone}}</span>
        <div class="value">{{summary.phone}}</div>
      </div>
      <div v-if="summary.email" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_book_email}}</span>
        <div class="value">{{summary.email}}</div>
      </div>
      <div class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_reservation_date}}</span>
        <div class="value">{{setDateTimeFormat(order.createat)}}</div>
      </div>
      <div v-if="order.cancelled && order.cancelledat" class="row notranslate">
        <span class="label toCapitalize">{{locale.reservation_cancel_date}}</span>
        <div class="value">{{setDateTimeFormat(order.cancelledat)}}</div>
      </div>
      <div v-if="cancellable && cancellable.isCancel && cancellable.ready && !(order.cancelled && order.cancelledat)" class="row notranslate">
        <div>
          <button
            type="submit"
            class="btn btnRound sizeS btnWhite"
            :disabled="!cancellable.isCancellable"
            @click="cancel">{{locale.reservation_cancel}}</button>
          <p class="cancelMessage mt5">{{cancellable.message}}</p>
        </div>
      </div>
      <div v-if="isPublic && orderStatus !== 'cancel'" class="qrInfo notranslate">
        <div>{{ locale.mypage_reservation_check_qrcode }}</div>
        <vue-qrcode
          v-if="qrCode"
          :value="qrCode"
          :scale="6" />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment/moment'
import { DateTime } from 'luxon'
import regExp from '@/common/regExp'
import VueQrcode from 'vue-qrcode'

export default {
  name: 'ReservationItem',
  props: [
    'order',
    'lang',
    'ln',
    'locale',
    'isPublic',
    'auth'
  ],
  components: {
    VueQrcode
  },
  computed: {
    pointColors () {
      return this.$store.getters['common/getPointColors']
    },
    summary () {
      return this.order.ordersheet.content
    },
    dateFormat () {
      const setting = this.$store.state.common.setting
      const date = setting.dateFormat[setting.dateformatDefault]
      return date
    },
    format () {
      const time = 'HH:mm:ss'
      return `${this.dateFormat} ${time}`
    },
    timezone () {
      return this.$store.state.siteInfo.timezoneId || ''
    },
    seats () {
      return this.autoAssign ? this.summary.seats[0] : this.summary.seats
    }
  },
  created () {
    this.init()
  },
  mounted () {
    this.$nextTick(() => {
      if (this.isPublic) {
        const authInfo = this.auth
        const win = window.location
        const reservationId = this.order && this.order.order_uuid
        let params = publicToOta(win.search)
        let url = ''
        // 공용디바이스 사용자는 개인링크로 변환 params
        function publicToOta (queryString) {
          const params = {}
          queryString.replace(/[#?&]+([^=&]+)=([^&]*)/gi, function (str, key, value) {
            params[key] = value
          })
          params.frame = false
          return params
        }
        delete params.public
        params.k = authInfo.k
        params = Object.entries(params).map(e => e.join('=')).join('&')
        url = `${win.origin}${win.pathname}?${params}#/${this.ln}/reservation/check/${reservationId}`
        // 공용디바이스 qr코드 생성
        this.$store.dispatch('util/getShortUrl', {
          url: url,
          days: 1
        }).then((res) => {
          if (res) {
            this.qrCode = res
          } else {
            this.qrCode = 'fail'
          }
        })
      }
    })
  },
  data () {
    return {
      orderStatus: 'ready',
      autoAssign: true,
      showPlaceName: false,
      showSeatName: false,
      cancellable: null,
      qrCode: null
    }
  },
  methods: {
    init () {
      const property = this.order.reservationSpaceId
      function getTimeDisplay (hhmm) {
        const time = hhmm.toString()
        return `${time.substr(0, 2)}:${time.substr(2, 2)}`
      }
      this.$store.dispatch('reservation/getReservationPropertyInfo', property).then((propertyInfo) => {
        const info = propertyInfo && propertyInfo.payload
        const settings = propertyInfo && propertyInfo.settings
        if (info) {
          // 예약취소 가능여부 확인
          const ready = this.order.order_status === 'ready'
          const date = this.order.ordersheet.content.date
          const time = this.order.ordersheet.content.time.id.substr(0, 4)
          const isCancel = info.cancel
          const cancelDay = info.cancelDay
          const cancelTime = info.cancelTime
          const endTime = moment().set({ hour: time.substr(0, 2), minute: time.substr(2, 2) }).add(-cancelTime, 'minutes')
          const beforeToday = moment().isBefore(moment(date).add(-cancelDay, 'days'), 'day')
          const isToday = moment().format('YYYY-MM-DD') === date
          const isTime = moment().isBefore(endTime)
          const limitDay = cancelDay > 0 ? this.setDateFormat(moment(date).add(-cancelDay, 'days').format('YYYY-MM-DD')) : this.setDateFormat(date)
          const limitTime = cancelDay > 0 ? '' : getTimeDisplay(endTime.format('HHmm'))
          const selectedSection = info.section.find(x => x.id === this.summary.seats[0].section.id)
          this.cancellable = {
            ready: ready,
            isCancel: isCancel,
            cancelDay: cancelDay,
            beforeToday: beforeToday,
            isToday: isToday,
            isTime: isTime,
            isCancellable: ready && (cancelDay <= 0 ? (beforeToday || isToday && isTime) : moment().isSameOrBefore(moment(date).add(-cancelDay, 'days'), 'day')),
            limitDay: limitDay,
            limitTime: limitTime,
            message: this.locale.message_reservation_cancel_time.replace('{var}', `${limitDay} ${limitTime}`)
          }
          // 섹션은 모두 동일하므로 첫번째 섹션의 배정정보로 설정
          this.autoAssign = selectedSection && selectedSection.autoAssign
          this.showPlaceName = selectedSection && selectedSection.showPlaceName
          this.showSeatName = selectedSection && selectedSection.showSeatName
          this.orderStatus = this.order.order_status
        }
      })
    },
    skinStyle (target, attr) {
      return this.pointColors(target, attr)
    },
    setDateFormat (date) {
      return moment(date).format(this.dateFormat.toUpperCase())
    },
    setDateTimeFormat (iso) {
      const submitAt = DateTime.fromISO(iso || moment().toISOString(), { zone: this.timezone })
      return submitAt.toFormat(this.format)
    },
    datetimeFormat (iso) {
      DateTime.fromISO(iso, { zone: this.timezone })
    },
    displayValue (order) {
      const lang = this.lang
      let value = order.value
      switch (order.type) {
        case 'rate':
          value = `${value.value} / ${value.max}`
          break
        case 'radio':
          value = value.label[lang] + (value.quantity ? ` (${value.quantity})` : '')
          break
        case 'checkbox':
          value = value.map(x => {
            const label = x.label[lang]
            const quantity = x.quantity ? `(${x.quantity})` : ''
            return `${label} ${quantity}`
          })
          break
        case 'select':
          value = value.label ? value.label[lang] : value
          break
        case 'calendar':
          value = DateTime.fromISO(value).toFormat(this.dateFormat)
          break
      }
      return value
    },
    priceFormat (price) {
      const code = this.currencyCode
      return regExp.priceFormat(price, code)
    },
    isEmpty (str) {
      return regExp.replaceAllEmpty(str).length <= 0
    },
    status (orderStatus) {
      const status = `reservation_status_${orderStatus}`
      return this.locale[status]
    },
    cancel () {
      const id = this.order.order_uuid
      if (window.confirm(this.locale.confirm_reservation_cancel)) {
        this.$emit('cancel', id)
        this.init()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.content .cartItem {
  .inner {
    display: block;
    padding-bottom: 20px;
  }
  .row {
    display: flex;
    & + .row {
      margin-top: 15px;
    }
  }
  .label {
    font-weight: bold;
  }
  .value {
    margin-left: 1em;
  }
}
.cancelMessage {
  font-size: 12px;
  color: $color-lgrey;
}
.qrInfo {
  display: block;
  margin-top: 15px;
  padding-top: 15px;
  font-size: 16px;
  color: $color-grey;
  text-align: center;
  border-top: 1px dashed $color-lgrey2;
}
</style>
